import { useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { createSpot, updateSpot, uploadImage, deleteImage, imgSrc } from "../api";

const ALLOWED_TAGS = ["gap", "rail", "ramp", "ledge"];

const pinIcon = L.divIcon({
  className: "spot-pin active",
  html: '<div class="pin"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 18],
});

function LocationPicker({ pos, setPos }) {
  useMapEvents({
    click(e) {
      setPos({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return pos ? <Marker position={[pos.lat, pos.lng]} icon={pinIcon} /> : null;
}

export default function SpotFormModal({ spot, onClose, onSaved, onPhotoChange }) {
  const editing = !!spot;
  const [images, setImages] = useState(spot?.images || []);
  const [name, setName] = useState(spot?.name || "");
  const [description, setDescription] = useState(spot?.description || "");
  const [tags, setTags] = useState(() =>
    (spot?.tags || "")
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter((t) => ALLOWED_TAGS.includes(t))
  );
  const toggleTag = (t) =>
    setTags((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
  const [pos, setPos] = useState(
    spot && spot.lat != null
      ? { lat: Number(spot.lat), lng: Number(spot.lng) }
      : null
  );
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const pickFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setPreview(f ? URL.createObjectURL(f) : null);
  };

  const removeImage = async (imgID) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await deleteImage(imgID);
      setImages((prev) => prev.filter((i) => i.imgID !== imgID));
      if (onPhotoChange) onPhotoChange();
    } catch (e2) {
      setErr(e2.message);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!name.trim()) return setErr("Please enter a name.");
    if (!pos) return setErr("Click on the map to set the location.");
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        lat: pos.lat,
        lng: pos.lng,
        tags: tags.join(","),
      };
      let spotID;
      if (editing) {
        await updateSpot(spot.spotID, payload);
        spotID = spot.spotID;
      } else {
        ({ spotID } = await createSpot(payload));
      }
      if (file) await uploadImage(spotID, file);
      onSaved(spotID);
    } catch (e2) {
      setErr(e2.message);
      setBusy(false);
    }
  };

  const center = pos ? [pos.lat, pos.lng] : [1.3521, 103.8198];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>{editing ? "Edit spot" : "Add a spot"}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <form className="form" onSubmit={submit}>
          <label>
            <span>Name *</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. East Coast Ledges"
              autoFocus
            />
          </label>

          <label>
            <span>Description</span>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's the spot like?"
            />
          </label>

          <div className="field">
            <span>Tags</span>
            <div className="tag-options">
              {ALLOWED_TAGS.map((t) => (
                <button
                  type="button"
                  key={t}
                  className={"tag-opt" + (tags.includes(t) ? " on" : "")}
                  onClick={() => toggleTag(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <span>Location * — click the map to set the pin</span>
            <div className="pick-map">
              <MapContainer center={center} zoom={pos ? 15 : 11}>
                <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                <LocationPicker pos={pos} setPos={setPos} />
              </MapContainer>
            </div>
            <small className="hint">
              {pos
                ? `📍 ${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}`
                : "No location selected yet"}
            </small>
          </div>

          {editing && images.length > 0 && (
            <div className="field">
              <span>Photos — tap ✕ to remove</span>
              <div className="edit-photos">
                {images.map((img) => (
                  <div className="edit-photo" key={img.imgID}>
                    <img src={imgSrc(img.imgURL)} alt="" />
                    <button
                      type="button"
                      className="del-photo"
                      onClick={() => removeImage(img.imgID)}
                      aria-label="Delete photo"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="field">
            <span>{editing ? "Add a photo (optional)" : "Photo (optional)"}</span>
            <label className="file-drop">
              {preview ? (
                <img src={preview} alt="preview" />
              ) : (
                <span>📷 Choose a photo…</span>
              )}
              <input type="file" accept="image/*" onChange={pickFile} hidden />
            </label>
            {file && <small className="hint">{file.name}</small>}
          </div>

          {err && <div className="form-error">{err}</div>}

          <div className="modal-actions">
            <button type="button" className="btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? "Saving…" : editing ? "Save changes" : "Add spot"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
